import React, { useCallback, useEffect, useState } from 'react';
/** ############################ REDUX ################################## */
import { batch } from 'react-redux';
import { clearAllLoadingAnalysesAction } from '../../../redux/slices/loadingAnalyses';
import { clearAllAnalysesAction } from '../../../redux/slices/analyses';
import { initialLoadAction } from '../../../redux/actions/collaboration';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
/** ############################ APOLLO ################################# */
import { useQuery } from '@apollo/client';
import { GET_WORKSPACE } from '../../../utils/apollo/workspaces';
import {
  IGetWorkspaceQuery,
  IGetWorkspaceQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
/** ############################ COMPONENTS ############################## */
import WorkspaceOptions from '../../../components/Workspace/WorkspaceOptions';
import WorkspaceContent from '../../../components/Workspace/WorkspaceContent';
import EditWorkspaceInfoModal from '../../../components/Workspace/EditWorkspaceInfoModal';
import {
  PageCreator,
  PageHeaderDivider,
  PageTitle,
} from '../../../components/PageHeader';
import Collaboration from '../../../components/Collaboration';
import * as WorkspaceUI from './WorkspaceUI';
import CropScreenshot from '../../../components/Collaboration/CropScreenshot';
/** ############################ UTILS ################################# */
import {
  INTERCOM_APP_ID,
  PAGE_BOTTOM_PADDING,
} from '../../../config/constants';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  clearWorkspace,
  populateWorkspace,
  renameWorkspace,
} from '../../../redux/slices/workspace';
import { useLoadWorkspaceAnalyses } from '../../../utils/hooks/workspaces';
import { useLocation, useParams } from 'react-router-dom';
import { assertNotNil } from '../../../utils/types';
import WorkspaceDeleteModal from '../../../components/Workspace/WorkspaceDeleteModal';
import { closeModal, ModalSliceType } from '../../../redux/slices/modal';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { layout } from '../../../config/styles';
import { setWorkspaceLastVisitedPathname } from '../../../redux/slices/workspaces';
import { ContentLoader } from '../../../components/Loaders';
import styled from 'styled-components';
import { workspaceViewed } from '../../../utils/analytics/events/workspace/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { useShowWorkspace } from '../../../utils/hooks';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { VerticalSeparatorDiv } from '../../DataModeler/DataModelerSchema/DataModelerCommonUI';
import WorkspaceToScratchpad from '../../../components/Workspace/WorkspaceToScratchpad';
import PageDescription from './PageDescription';
import Banner, { BannerUI } from '../../../components/Banner';

const LoadingContainer = styled.div`
  padding: 20px;
`;

export default function Workspace() {
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const location = useLocation();
  const { track } = useAnalytics();
  const workspaceId = urlParams?.workspaceId;
  assertNotNil(workspaceId);
  /* Fetch the first n workspaces meta info only and then redirect to the first */
  const { workspaceList, lastVisitedWorkspacePathname } = useAppSelector(
    (state) => state.workspaces,
  );
  const loadWorkspaceAnalyses = useLoadWorkspaceAnalyses();
  const isWorkspaceEnabled = useShowWorkspace();

  const [showBanner, setShowBanner] = useState(true);

  const currentWorkspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const isEditWorkspaceInfoModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.WorkspaceEdit, ''),
  );
  const isDeleteWorkspaceModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.WorkspaceDelete, ''),
  );
  const isEditing = useAppSelector((state) =>
    workspacePropertySelector(state, 'isEditing'),
  );
  const cachedName = useAppSelector((state) =>
    workspacePropertySelector(state, 'name'),
  );
  const cachedDescription = useAppSelector((state) =>
    workspacePropertySelector(state, 'description'),
  );
  const creator = useAppSelector((state) =>
    workspacePropertySelector(state, 'creator'),
  );
  const snapshotName = useAppSelector((state) =>
    workspacePropertySelector(state, 'snapshot'),
  )?.name;

  /* Fetch the workspace meta */
  const populateWorkspaceInStore = useCallback(
    ({ workspace }: IGetWorkspaceQuery) => {
      if (workspace) {
        const { main, ...workspaceData } = workspace;

        batch(() => {
          dispatch(populateWorkspace(workspaceData));
          dispatch(initialLoadAction({ workspace }));
          dispatch(clearAllAnalysesAction());

          loadWorkspaceAnalyses(workspace.rows);
        });

        track(workspaceViewed(workspace));
      }
    },
    [dispatch, loadWorkspaceAnalyses, track],
  );

  useQuery<IGetWorkspaceQuery, IGetWorkspaceQueryVariables>(GET_WORKSPACE, {
    variables: {
      id: workspaceId,
      withLastViewedAt: true,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: populateWorkspaceInStore,
    skip: !workspaceList?.length,
  });

  const onCloseModal = () => {
    dispatch(closeModal());
  };

  const onWorkspaceRename = (name: string, description: string) => {
    dispatch(renameWorkspace({ name, description }));
    onCloseModal();
  };

  useEffect(() => {
    if (lastVisitedWorkspacePathname !== location.pathname) {
      dispatch(
        setWorkspaceLastVisitedPathname({
          lastVisitedPathname: location.pathname,
        }),
      );
    }
  }, [dispatch, location.pathname, lastVisitedWorkspacePathname]);

  useEffect(() => {
    return () => {
      batch(() => {
        dispatch(clearWorkspace());
        dispatch(clearAllLoadingAnalysesAction());
        dispatch(clearAllAnalysesAction());
      });
    };
  }, [dispatch, workspaceId]);

  useEffect(() => {
    // move Intercom to the left to avoid overlapping
    window.Intercom('update', {
      app_id: INTERCOM_APP_ID,
      horizontal_padding:
        layout.collaborationWidth + layout.mainContainerHorizontalPadding,
    });

    // set Intercom back to the default position
    return () => {
      window.Intercom('update', {
        app_id: INTERCOM_APP_ID,
        horizontal_padding: layout.intercomPadding,
      });
    };
  }, []);

  useEffect(() => {
    track(
      pageViewed({
        category: 'workspaces',
        name: 'workspace',
        id: workspaceId,
      }),
    );
  }, [track, workspaceId]);

  if (currentWorkspaceId !== workspaceId) {
    return (
      <LoadingContainer>
        <ContentLoader />
      </LoadingContainer>
    );
  }

  // KBT-567 - hide workspaces page if feature flag is disabled
  if (!isWorkspaceEnabled) {
    return null;
  }
  return (
    // display the border of header since header
    <div>
      <WorkspaceUI.LeftContainer>
        {showBanner && (
          <Banner
            variant="warning"
            centerContent
            close={() => setShowBanner(false)}
          >
            <BannerUI.Highlight>Workspaces</BannerUI.Highlight> will be
            decommissioned and no longer available on April 10th. To retain your
            work, consider converting them to{' '}
            <BannerUI.Link
              target="_blank"
              to="https://docs.kubit.ai/docs/scratchpad"
              trackContext="workspace"
              trackAction="open scratchpad documentation"
            >
              Scratchpad
            </BannerUI.Link>
          </Banner>
        )}
        <WorkspaceUI.WorkspaceHeader id="workspace-header">
          <PageTitle title={cachedName}>
            {isEditing && (
              <WorkspaceUI.WorkspaceSaveTip>
                (Unsaved Draft)
              </WorkspaceUI.WorkspaceSaveTip>
            )}
            <WorkspaceUI.WorkspaceOptionMenuWrapper>
              <>
                <WorkspaceToScratchpad />
                <VerticalSeparatorDiv layout="vertical" size="short" />
              </>
              {!isEditing && <CropScreenshot hasContainer={true} />}
              <WorkspaceOptions />
            </WorkspaceUI.WorkspaceOptionMenuWrapper>
          </PageTitle>
          {creator && <PageCreator creator={creator} />}
          {cachedDescription && (
            <PageDescription>{cachedDescription}</PageDescription>
          )}
          <PageHeaderDivider />
        </WorkspaceUI.WorkspaceHeader>
        <WorkspaceUI.WorkspaceChartWrapper>
          <WorkspaceContent />
        </WorkspaceUI.WorkspaceChartWrapper>
        <div style={{ paddingBottom: PAGE_BOTTOM_PADDING }} />
      </WorkspaceUI.LeftContainer>
      <WorkspaceUI.ContainerRight id={'collaboration-right-container'}>
        <Collaboration />
      </WorkspaceUI.ContainerRight>

      {isDeleteWorkspaceModalOpen && (
        <WorkspaceDeleteModal
          workspaceName={cachedName}
          onClose={onCloseModal}
          workspaceId={workspaceId}
        />
      )}

      {isEditWorkspaceInfoModalOpen && (
        <EditWorkspaceInfoModal
          closeWindow={onCloseModal}
          currentName={cachedName}
          snapshotName={snapshotName}
          currentDescription={cachedDescription}
          onChangeInfo={onWorkspaceRename}
        />
      )}
    </div>
  );
}
